// Agent tool-posture renderer. fn-52.3.
//
// Lifted verbatim from `pages/runs/detail.tsx` (fn-2.x) so both the
// run-detail header AND the agent details view (`/agents/:id`) render
// the agent's tool/toolset permission policy from one source. The
// run-detail page re-exports these for backward compatibility.
//
// **Source shape** (per `agents.config.tools[*]`):
//   - Each toolset entry has `configs[*]` (per-tool override) and a
//     `default_config` (the policy for anything not in `configs`).
//   - `permission_policy.type` is one of `always_allow`, `always_ask`,
//     `always_deny` (verified 2026-05-17 against MA wire).
//   - When `configs` is absent or empty, the chip row shows just the
//     default policy + the toolset id.
//
// The panel collapses when the config is empty / unparseable; we don't
// want to render a confusing "No tools" line for the common case where
// the API returns an agent we haven't yet wired tool-policy reading for.

import { Fragment } from 'react';

export type ToolPolicy = 'allow' | 'ask' | 'deny' | 'unknown';

export interface ToolsetPostureRow {
  readonly toolset: string;
  readonly defaultPolicy: ToolPolicy;
  readonly overrides: ReadonlyArray<{ readonly name: string; readonly policy: ToolPolicy }>;
}

/**
 * Parse the agent's `config.tools` into a render-ready posture model.
 * Pure + exported so it unit-tests without mounting the panel. Returns
 * an empty array when the config is null/unparseable/has no tools — the
 * panel treats that as "nothing to show" and renders nothing.
 */
export function readToolsetPosture(config: Record<string, unknown> | null): ToolsetPostureRow[] {
  if (config === null || typeof config !== 'object') return [];
  const tools = (config as { tools?: unknown }).tools;
  if (!Array.isArray(tools) || tools.length === 0) return [];

  const rows: ToolsetPostureRow[] = [];
  for (const t of tools) {
    if (t === null || typeof t !== 'object') continue;
    const tt = t as {
      type?: unknown;
      default_config?: unknown;
      configs?: unknown;
    };
    const toolsetName = typeof tt.type === 'string' ? tt.type : 'tools';
    const defaultPolicy = readPolicy(tt.default_config);
    const overrides: Array<{ name: string; policy: ToolPolicy }> = [];
    if (Array.isArray(tt.configs)) {
      for (const c of tt.configs) {
        if (c === null || typeof c !== 'object') continue;
        const cc = c as { name?: unknown; permission_policy?: unknown };
        if (typeof cc.name !== 'string') continue;
        overrides.push({
          name: cc.name,
          policy: readPolicyValue(cc.permission_policy),
        });
      }
    }
    rows.push({ toolset: toolsetName, defaultPolicy, overrides });
  }
  return rows;
}

export function readPolicy(defaultConfig: unknown): ToolPolicy {
  if (defaultConfig === null || typeof defaultConfig !== 'object') return 'unknown';
  return readPolicyValue((defaultConfig as { permission_policy?: unknown }).permission_policy);
}

export function readPolicyValue(p: unknown): ToolPolicy {
  if (p === null || typeof p !== 'object') return 'unknown';
  const t = (p as { type?: unknown }).type;
  if (t === 'always_allow') return 'allow';
  if (t === 'always_ask') return 'ask';
  if (t === 'always_deny') return 'deny';
  return 'unknown';
}

export function humanToolsetName(name: string): string {
  // Strip date suffixes like _20260401 for readability.
  return name.replace(/_\d{8}$/, '').replace(/_/g, ' ');
}

/**
 * fn-2.x — render the agent's tool posture so operators can see at a
 * glance what the agent can and cannot do. Returns null when the
 * config has no parseable tools.
 */
export function AgentToolsPanel({
  agent,
}: {
  agent: { config: Record<string, unknown> | null; name: string } | null;
}): React.ReactElement | null {
  if (agent === null) return null;
  const rows = readToolsetPosture(agent.config);
  if (rows.length === 0) return null;

  return (
    <div className="agent-tools-panel" aria-label={`Tool posture for ${agent.name}`}>
      <span className="agent-tools-panel-label">Tools</span>
      {rows.map((row) => (
        <Fragment key={row.toolset}>
          <span className="agent-tools-panel-toolset" title={row.toolset}>
            {humanToolsetName(row.toolset)} default:
          </span>
          <PolicyChip policy={row.defaultPolicy} />
          {row.overrides.map((o) => (
            <PolicyChip key={`${row.toolset}/${o.name}`} policy={o.policy} toolName={o.name} />
          ))}
        </Fragment>
      ))}
    </div>
  );
}

export function PolicyChip({
  policy,
  toolName,
}: {
  policy: ToolPolicy;
  toolName?: string;
}): React.ReactElement {
  const glyph = policy === 'allow' ? '✓' : policy === 'ask' ? '⏸' : policy === 'deny' ? '✗' : '·';
  const label =
    policy === 'allow'
      ? 'Auto-allow'
      : policy === 'ask'
        ? 'Asks'
        : policy === 'deny'
          ? 'Deny'
          : 'Unknown';
  return (
    <span
      className={`agent-tool-chip agent-tool-chip--${policy}`}
      title={`${toolName ?? 'default'}: ${label}`}
    >
      {glyph} {toolName ?? label}
      {toolName !== undefined && <span className="agent-tool-chip-policy"> · {label}</span>}
    </span>
  );
}
